import * as Sentry from "@sentry/react";

declare const __APP_VERSION__: string;

type SentryUser = {
  id?: string | number;
  email?: string;
  username?: string;
};

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const env = (window as any)?.env ?? {};
  const envType: string = (env.VITE_TYPE || import.meta.env.VITE_TYPE || "").toUpperCase();
  const dsn: string = env.VITE_SENTRY_DSN || import.meta.env.VITE_SENTRY_DSN || "";

  const sentryEnabledEnvs = ["STAGING", "PRODUCTION"];
  if (!sentryEnabledEnvs.includes(envType) || !dsn) return;

  Sentry.init({
    dsn,
    environment: envType.toLowerCase(),
    release: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Scripts we load but do not own. Sentry's browserApiErrors integration
    // wraps setTimeout, so a throw inside a third-party timer callback is
    // reported against us even when no frame in the stack is our code and no
    // user-visible behaviour changed. Matched on the innermost frame's URL, so
    // anything with a frame of ours still reports normally.
    denyUrls: [
      // Google reCAPTCHA
      /\/recaptcha\//,
      /www\.gstatic\.com\/recaptcha/,
      // Browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-(web-)?extension:\/\//,
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });

  initialized = true;
}

export function isSentryActive(): boolean {
  return initialized;
}

export function setSentryUser(user: SentryUser | null | undefined): void {
  if (!initialized || !user) return;
  Sentry.setUser({
    id: user.id != null ? String(user.id) : undefined,
    email: user.email,
    username: user.username,
  });
}

export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(err, context ? { extra: context } : undefined);
}

export { Sentry };
